import React from 'react';
import { View, Text, TextInput, Keyboard } from 'react-native';
import { Card, Button } from 'react-native-elements';
import { Loading } from '@double_point/rn-components';
import { t } from 'i18next';
import { styles } from '../styles/components/loginWithVenuePartner';
import AppContext from 'contexts/AppContext';
import { useFormik } from 'formik';
import { VenuePartnerLoginForm } from 'forms/schemas/VenuePartnerLoginForm';
import LoginWithVenuePartnerProps from 'types/components/LoginWithVenuePartnerProps';
import { tomorrowUserLogin } from 'services/authService';
import useChangeAuthData from 'hooks/useChangeAuthData';
import AuthData from 'types/models/AuthData';
import { getUserTomorrowConsumptions, userEnabledToTomorrowBenefitsCheck } from 'services/venuesServices';

export default ({}: LoginWithVenuePartnerProps): JSX.Element => {
  const { currentVenue, setModalizeData } = React.useContext(AppContext);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const { storeCurrentAuthData } = useChangeAuthData();

  const venueTomorrowPartnerMainDocumentType =
    (currentVenue?.tomorrowPartnerMainDocumentType as string) ?? t('identification-number');

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      identificationNumber: '',
    },
    onSubmit: () => {
      onConfirm();
    },
    validationSchema: VenuePartnerLoginForm(venueTomorrowPartnerMainDocumentType),
    validateOnBlur: false,
    validateOnChange: false,
  });

  const onConfirm = async (): Promise<void> => {
    if (!currentVenue?.id) return;

    setIsLoading(true);
    try {
      const body = { ...formik.values, venueId: currentVenue.id };
      let authData = await tomorrowUserLogin(body);
      if (authData) {
        authData = await userEnableToTomorrowBenefits(authData);
        authData.user.realPassword = formik.values.password;
        await storeCurrentAuthData({ ...authData });
        setModalizeData(null);
      }
    } catch (error: any) {
      console.log('error', error);
      if (error.error) {
        setErrorMessage(error.error);
      } else {
        setErrorMessage(t('phone-number-modal-error-on-login'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const userEnableToTomorrowBenefits = async (localAuthData: AuthData) => {
    const result = await userEnabledToTomorrowBenefitsCheck(localAuthData.user.id);
    const currentUser = { ...localAuthData.user, enabledToTomorrowBenefits: result };
    if (result) {
      const consumptions = await getUserTomorrowConsumptions(localAuthData.user.id);
      currentUser.tomorrowConsumptions = consumptions;

      localAuthData = { ...localAuthData, user: currentUser };
    }

    return localAuthData;
  };

  // Partner form
  const loginForm = () => {
    return (
      <View style={styles.container}>
        {isLoading && <Loading />}
        <View style={styles.loginFormContainer}>
          {errorMessage && (
            <View style={styles.loginErrorTextContainer}>
              <Text style={styles.loginErrorText}>{errorMessage}</Text>
            </View>
          )}
          <View style={styles.textInputContainer}>
            <TextInput
              keyboardType="default"
              style={[styles.textInput]}
              onChangeText={formik.handleChange('email')}
              placeholder={venueTomorrowPartnerMainDocumentType}
              value={formik.values.email}
            />
            {formik.errors.email && formik.errors.email.length > 0 && (
              <View style={styles.errorTextContainer}>
                <Text style={styles.errorText}>{formik.errors.email}</Text>
              </View>
            )}
          </View>

          <View style={styles.textInputContainer}>
            <TextInput
              secureTextEntry={true}
              style={[styles.textInput]}
              onChangeText={formik.handleChange('password')}
              placeholder={t('password')}
              value={formik.values.password}
            />
            {formik.errors.password && formik.errors.password.length > 0 && (
              <View style={styles.errorTextContainer}>
                <Text style={styles.errorText}>{formik.errors.password}</Text>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  };

  return (
    <>
      <Card.Title style={styles.legendText}>{`${t('tomorrow-login-button', {
        venueName: currentVenue?.name,
      })}`}</Card.Title>
      <Card.Divider />
      <View style={styles.phoneInputContainer}>{loginForm()}</View>
      <View style={styles.btnContainer}>
        <Button
          title={t('confirm-code')}
          buttonStyle={[styles.btn, { backgroundColor: currentVenue?.appColor }]}
          titleStyle={[styles.btnText]}
          disabledStyle={styles.disabledBtn}
          disabledTitleStyle={styles.btnText}
          onPress={async () => {
            Keyboard.dismiss();
            formik.handleSubmit();
          }}
        />
      </View>
    </>
  );
};
